/**
 * TTS Context
 *
 * Manages the user's preferred Kokoro TTS voice across settings and the reader.
 * The voice selection is persisted to localStorage under 'vellum:ttsVoice'.
 *
 * All TTS processing is performed entirely client-side via kokoro-js (WebAssembly).
 * No journal text is ever transmitted to a third party.
 *
 * Usage:
 *   const { selectedVoice, setVoice } = useTTSContext();
 */

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

// ── Voice definitions ─────────────────────────────────────────────────────────

// `country` is a 2-letter ISO country code rendered as a small chip in the
// voice picker. We deliberately avoid flag emojis here — they render
// inconsistently across platforms and don't fit our icon-driven UI.
export const TTS_VOICES = [
  { id: 'af_bella', name: 'Bella', accent: 'American', gender: 'female' as const, country: 'US' },
  { id: 'af_sarah', name: 'Sarah', accent: 'American', gender: 'female' as const, country: 'US' },
  { id: 'af_sky', name: 'Sky', accent: 'American', gender: 'female' as const, country: 'US' },
  { id: 'am_adam', name: 'Adam', accent: 'American', gender: 'male' as const, country: 'US' },
  { id: 'am_michael', name: 'Michael', accent: 'American', gender: 'male' as const, country: 'US' },
  { id: 'bf_emma', name: 'Emma', accent: 'British', gender: 'female' as const, country: 'GB' },
  {
    id: 'bf_isabella',
    name: 'Isabella',
    accent: 'British',
    gender: 'female' as const,
    country: 'GB',
  },
  { id: 'bm_george', name: 'George', accent: 'British', gender: 'male' as const, country: 'GB' },
  { id: 'bm_lewis', name: 'Lewis', accent: 'British', gender: 'male' as const, country: 'GB' },
] as const;

export type TTSVoiceId = (typeof TTS_VOICES)[number]['id'];
export type TTSVoice = (typeof TTS_VOICES)[number];

export const DEFAULT_TTS_VOICE: TTSVoiceId = 'af_bella';

export function getTTSVoice(id: TTSVoiceId): TTSVoice {
  return TTS_VOICES.find((v) => v.id === id) ?? TTS_VOICES[0];
}

// ── Context ───────────────────────────────────────────────────────────────────

interface TTSContextValue {
  selectedVoice: TTSVoiceId;
  setVoice: (id: TTSVoiceId) => void;
}

const TTSContext = createContext<TTSContextValue | null>(null);

const STORAGE_KEY = 'vellum:ttsVoice';

function readStoredVoice(): TTSVoiceId {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && TTS_VOICES.some((v) => v.id === stored)) {
      return stored as TTSVoiceId;
    }
  } catch {
    // localStorage may be blocked (private browsing, etc.)
  }
  return DEFAULT_TTS_VOICE;
}

// ── Provider ──────────────────────────────────────────────────────────────────

export function TTSProvider({ children }: { children: React.ReactNode }) {
  const [selectedVoice, setSelectedVoice] = useState<TTSVoiceId>(readStoredVoice);

  const setVoice = useCallback((id: TTSVoiceId) => {
    setSelectedVoice(id);
    try {
      localStorage.setItem(STORAGE_KEY, id);
    } catch {
      // Ignore storage errors
    }
  }, []);

  const value = useMemo(() => ({ selectedVoice, setVoice }), [selectedVoice, setVoice]);

  return <TTSContext.Provider value={value}>{children}</TTSContext.Provider>;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useTTSContext(): TTSContextValue {
  const ctx = useContext(TTSContext);
  if (!ctx) throw new Error('useTTSContext must be used within a <TTSProvider>');
  return ctx;
}
